/**
 * ProfileCompletion.js — Profile completion progress bar component
 * Place at: src/public/components/ProfileCompletion.js
 *
 * Shows as a banner on CommerceProfile and homepage
 * when user has incomplete profile fields
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const B = '#2563EB';

const getSteps = (user, role, t) => {
  const steps = [
    {
      key: 'phone',
      label: t('profile_completion.step_phone'),
      icon: '📱',
      done: !!user?.isVerified,
      page: null, // happens via OTP at registration
    },
    {
      key: 'name',
      label: t('profile_completion.step_name'),
      icon: '👤',
      done: !!(user?.name && user.name.trim().length > 2),
      page: 'CustomerProfile',
    },
    {
      key: 'photo',
      label: t('profile_completion.step_photo'),
      icon: '📸',
      done: !!user?.avatarUrl,
      page: 'CustomerProfile',
    },
    {
      key: 'location',
      label: t('profile_completion.step_location'),
      icon: '📍',
      done: !!user?.city,
      page: 'CustomerProfile',
    },
    {
      key: 'bio',
      label: t('profile_completion.step_bio'),
      icon: '✏️',
      done: !!user?.bio,
      page: 'CustomerProfile',
    },
  ];

  // Seller-specific
  if (['seller', 'admin', 'manager'].includes(role)) {
    steps.push({
      key: 'store',
      label: t('profile_completion.step_store'),
      icon: '🏪',
      done: !!(user?.storeName && user?.storeDescription),
      page: 'StoreSettings',
    });
  }

  return steps;
};

const ProfileCompletion = ({ currentUser, userRole, onNavigate, compact = false, personalProfile }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !currentUser) return null;

  // Account setup is canonical: once onboarding has been completed on the
  // User record, do not resurrect an "Account Setup" prompt by recomputing
  // completion from seller/store-shaped or legacy profile fields.
  if (!personalProfile && currentUser.onboardingCompleted) return null;

  const steps = personalProfile ? [
    { key: 'name', label: t('profile_completion.step_name'), done: !!personalProfile.displayName?.trim(), page: 'CustomerProfile' },
    { key: 'photo', label: t('profile_completion.step_photo'), done: !!personalProfile.photoUrl, page: 'CustomerProfile' },
    { key: 'username', label: t('profile_completion.step_username'), done: !!personalProfile.username, page: 'CustomerProfile' },
    { key: 'location', label: t('profile_completion.step_location'), done: !!personalProfile.location?.trim(), page: 'CustomerProfile' },
    { key: 'bio', label: t('profile_completion.step_bio'), done: !!personalProfile.bio?.trim(), page: 'CustomerProfile' },
  ] : getSteps(currentUser, userRole, t);
  const done   = steps.filter(s => s.done).length;
  const total  = steps.length;
  const pct    = Math.round((done / total) * 100);
  const nextStep = steps.find(s => !s.done);

  // Don't show if 100% complete
  if (!personalProfile && pct === 100) return null;

  if (compact) {
    return (
      <div style={{ backgroundColor:'#fff', borderRadius:12, padding:'12px 16px',
        marginBottom:12, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center', marginBottom:8 }}>
          <span style={{ fontSize:13, fontWeight:800, color:'#1e293b' }}>
            {t(personalProfile ? 'profile_completion.personal_title' : 'profile_completion.compact_title', { percent: pct })}
          </span>
          <span style={{ fontSize:12, color:'#64748b' }}>{done}/{total}</span>
        </div>
        <div style={{ height:6, backgroundColor:'#f1f5f9', borderRadius:100 }}>
          <div style={{ height:'100%', borderRadius:100, backgroundColor:B,
            width:`${pct}%`, transition:'width 0.5s' }} />
        </div>
        {personalProfile && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:12 }}>
            {steps.map(step => (
              <button key={step.key} type="button" onClick={() => onNavigate(step.page, { editField: step.key })}
                style={{ border:'1px solid #dbeafe', borderRadius:8, background:step.done ? '#f0fdf4' : '#eff6ff',
                  color:step.done ? '#166534' : B, padding:'8px 10px', fontSize:14, cursor:'pointer' }}>
                {step.done ? '✓ ' : ''}{step.label}
              </button>
            ))}
          </div>
        )}
        {(nextStep || personalProfile) && (
          <button onClick={() => { const page = nextStep ? nextStep.page : 'CustomerProfile'; if (page) onNavigate(page, { editField: nextStep?.key || 'name' }); }}
            style={{ marginTop:8, background:'none', border:'none',
              cursor:'pointer', color:B, fontSize:12, fontWeight:700,
              padding:0 }}>
            {nextStep?.label || t('profile_completion.edit_personal')} →
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ backgroundColor:'#eff6ff', borderRadius:16,
      padding:'16px 20px', marginBottom:16,
      border:'1px solid #bfdbfe', position:'relative' }}>

      <button onClick={() => setDismissed(true)}
        style={{ position:'absolute', top:12, right:12,
          background:'none', border:'none', cursor:'pointer',
          color:'#94a3b8', fontSize:18, lineHeight:1 }}>×</button>

      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <div style={{ fontSize:20 }}>👤</div>
        <div>
          <div style={{ fontSize:14, fontWeight:900, color:'#1e293b' }}>
            {t('profile_completion.full_title', { percent: pct })}
          </div>
          <div style={{ fontSize:12, color:'#64748b' }}>
            {t('profile_completion.full_desc')}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height:8, backgroundColor:'#dbeafe', borderRadius:100, marginBottom:12 }}>
        <div style={{ height:'100%', borderRadius:100, backgroundColor:B,
          width:`${pct}%`, transition:'width 0.5s' }} />
      </div>

      {/* Steps */}
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {steps.map(s => (
          <div key={s.key}
            onClick={() => !s.done && s.page && onNavigate(s.page, { editField: s.key })}
            style={{ display:'flex', alignItems:'center', gap:10,
              cursor: s.done || !s.page ? 'default' : 'pointer' }}>
            <div style={{ width:20, height:20, borderRadius:'50%', flexShrink:0,
              backgroundColor: s.done ? '#16a34a' : '#e2e8f0',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, color: s.done ? '#fff' : '#94a3b8' }}>
              {s.done ? '✓' : s.icon}
            </div>
            <span style={{ fontSize:12,
              color: s.done ? '#64748b' : '#1e293b',
              fontWeight: s.done ? 400 : 700,
              textDecoration: s.done ? 'line-through' : 'none' }}>
              {s.label}
            </span>
            {!s.done && s.page && (
              <span style={{ fontSize:10, color:B, fontWeight:700, marginLeft:'auto' }}>
                {t('profile_completion.do_it_button')}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileCompletion;